import { i18n } from '@/i18n'
import { defaultCurrency } from '@/i18n'
import type { CurrencyCode } from '@/types'
import { getTheme } from '@goodlifeplus/themes'
import type { SiteSlug, ThemeType } from '@goodlifeplus/themes/types'
import { toValue } from 'vue'
import type { Locale } from 'vue-i18n'

export class SettingsService {
  private static disabledRoutesMap: Record<Locale | ThemeType, string[]> = {
    'en-GB': [],
    'en-IE': ['rewards', 'daily-prizes', 'as-seen-in', 'membership-prizes'],
    'en-CA': [
      'good-causes',
      'as-seen-in',
      'sponsorship',
      'winners',
      'stats-banner',
      'trustpilot',
    ],
    club: ['good-causes'],
  }

  public static getLocale(): Locale {
    return toValue(i18n.global.locale) ?? 'en-GB'
  }

  public static isGbLocale(): boolean {
    return SettingsService.getLocale() === 'en-GB'
  }

  public static isIeLocale(): boolean {
    return SettingsService.getLocale() === 'en-IE'
  }

  public static isCaLocale(): boolean {
    return SettingsService.getLocale() === 'en-CA'
  }

  public static getDisabledRoutes(): string[] {
    const locale = SettingsService.getLocale()
    const { type, currency } = getTheme(
      import.meta.env.GLP_SITE_SLUG as SiteSlug
    )
    return [
      ...(SettingsService.disabledRoutesMap[locale] ?? []),
      ...(SettingsService.disabledRoutesMap[type] ?? []),
      ...(SettingsService.disabledRoutesMap[currency ?? ''] ?? []),
    ]
  }

  public static isSectionEnabled(sectionName: string): boolean {
    const disabledRoutes = SettingsService.getDisabledRoutes()
    return !disabledRoutes.includes(sectionName)
  }

  /**
   * Fetches the site pixel ID from the CMS settings endpoint.
   *
   * @returns {Promise<string>} The site pixel ID. Default to GLP pixel ID if not found.
   */
  public static async getSitePixelId(): Promise<string | null> {
    // try {
    //   const { data, error } = await fetcher.get<{ pixel_id: string }>(
    //     toCmsUrl(`/site-config`)
    //   )

    //   if (error.value || !data.value || !data.value.pixel_id) {
    //     throw new Error('Failed to fetch site pixel ID')
    //   }

    //   return data.value.pixel_id
    // } catch (error) {
    //   return import.meta.env.GLP_PIXEL_ID ?? null
    // }

    return import.meta.env.GLP_PIXEL_ID ?? null
  }

  public static getSiteTagId(): string | null {
    return import.meta.env.GLP_GA_TAG_ID ?? null
  }

  public static getSiteMeasurementId(): string | null {
    return import.meta.env.GLP_GA_MEASUREMENT_ID ?? null
  }

  public static get activeCurrency(): CurrencyCode {
    return defaultCurrency
  }

  public static get currencySymbol(): string {
    return (
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency: defaultCurrency,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? defaultCurrency
    )
  }

  public static get timezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  // Static properties for one-time use
  public static disabledRoutes = SettingsService.getDisabledRoutes()
}
